import React, { useState, useEffect, useCallback } from 'react';
import styles from './EmployerDetails.module.scss';
import PageTitle from '../../../components/pageTitle/PageTitle';
import Search from '../../../components/search/Search';
import Table from '../../../components/table/Table';
import DropDownButton from '../../../components/dropDownButton/DropDownButton';
import ProfilePicture from '../../../components/profilePicture/ProfilePicture';
import Tag from '../../../components/tag/Tag';
import Filter from '../../../components/filter/Filter';
import Button from '../../../components/button/Button';
import Icon from '../../../components/icon/Icon';
import Modal from '../../../components/modal/Modal';
import axiosClient from '../../../api/axiosClient';
import { useToasts } from '../../../contexts/ToastContext';
import { useProgram } from '../../../contexts/ProgramContext';
import LoadingOverlay from '../../../components/loadingOverlay/LoadingOverlay';

const STATUS_FILTERS = [
  { label: 'Not Requested', value: 'not_requested' },
  { label: 'Requested', value: 'requested' },
  { label: 'Submitted', value: 'submitted' },
];

const STATUS_TAG = {
  not_requested: { text: 'Not Requested', variant: 2 },
  requested: { text: 'Requested', variant: 3 },
  submitted: { text: 'Submitted', variant: 5 },
};

const EmployerDetails = () => {
  const { showToast } = useToasts();
  const { programs, selectedProgramId, setSelectedProgramId } = useProgram();
  const [students, setStudents] = useState([]);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('not_requested');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1, total: 0, start: 1, end: 1, hasPrev: false, hasNext: false
  });

  const fetchStudents = useCallback(async (page = 1) => {
    if (!selectedProgramId) return;
    setIsLoading(true);
    try {
      const params = { programId: selectedProgramId, status, page, limit: 20 };
      if (search) params.search = search;
      const response = await axiosClient.get('/employer-details/students', { params });
      const responseData = response.data;
      setStudents(responseData.data || []);
      const totalPages = responseData.totalPages;
      setPagination({
        current: responseData.page,
        total: responseData.total,
        start: (responseData.page - 1) * responseData.limit + 1,
        end: Math.min(responseData.page * responseData.limit, responseData.total),
        hasPrev: responseData.page > 1,
        hasNext: responseData.page < totalPages,
      });
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [selectedProgramId, search, status, showToast]);

  useEffect(() => {
    setSelectedStudentIds([]);
    fetchStudents(1);
  }, [fetchStudents]);

  const handleSendRequests = async () => {
    setIsSending(true);
    try {
      await axiosClient.post('/employer-details/send-requests', { userIds: selectedStudentIds });
      showToast('Requests sent successfully');
      setShowConfirmModal(false);
      setSelectedStudentIds([]);
      fetchStudents(1);
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setIsSending(false);
    }
  };

  const programOptions = programs.map(p => ({
    label: p.title,
    onClick: () => setSelectedProgramId(p.id),
  }));

  const selectedProgramTitle = programs.find(p => p.id === selectedProgramId)?.title || 'Select Program';

  const columns = [
    {
      header: 'Student',
      key: 'student',
      render: (row) => (
        <div className={styles.studentCell}>
          <ProfilePicture
            firstName={row.firstName}
            lastName={row.lastName}
            src={row.profilePicture}
            size="xs"
          />
          {status === 'submitted' && row.employerDetails ? (
            <span className={styles.nameLink} onClick={() => setSelectedDetail(row.employerDetails)}>
              {row.firstName} {row.lastName}
            </span>
          ) : (
            <span>{row.firstName} {row.lastName}</span>
          )}
        </div>
      ),
    },
    {
      header: 'Email',
      key: 'email',
      render: (row) => row.email || '—',
    },
    {
      header: 'Status',
      key: 'employerDetailsStatus',
      render: (row) => {
        const tag = STATUS_TAG[row.employerDetailsStatus] || { text: row.employerDetailsStatus, variant: 2 };
        return <Tag text={tag.text} variant={tag.variant} rounded />;
      },
    },
  ];

  const isSelectable = status === 'not_requested' || status === 'requested';

  return (
    <>
      {isLoading && <LoadingOverlay />}
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <PageTitle label="Employer Details" />
          <DropDownButton
            label={selectedProgramTitle}
            items={programOptions}
            type="button"
            size="small"
            variant="primary-light"
            position="bottom-left"
          />
        </div>
        <div className={styles.headerActions}>
          <Search onSubmit={setSearch} />
        </div>
      </div>
      <div className={styles.container}>
        <div className={styles.topBar}>
          <div className={styles.filter}>
            <Filter
              filters={STATUS_FILTERS}
              selected={status}
              onChange={(value) => {
                setStatus(value);
                setSelectedStudentIds([]);
              }}
            />
          </div>
          {isSelectable && selectedStudentIds.length > 0 && (
            <div className={styles.selectionActions}>
              <span className={styles.selectionCount}>{selectedStudentIds.length} Selected</span>
              <div className={styles.sendBtnWrapper}>
                <Button
                  label={
                    <span className={styles.sendBtn}>
                      <Icon name="arrow-up-right" />
                      {status === 'requested' ? 'Re-request' : 'Send Request'}
                    </span>
                  }
                  variant="primary"
                  size="small"
                  onClick={() => setShowConfirmModal(true)}
                />
              </div>
            </div>
          )}
        </div>
        <Table
          columns={columns}
          data={students}
          pagination={pagination}
          onPageChange={(page) => fetchStudents(page)}
          onRowSelect={isSelectable ? setSelectedStudentIds : undefined}
          selectedRows={selectedStudentIds}
          emptyMessage="No students found."
        />
      </div>

      {showConfirmModal && (
        <Modal>
          {isSending && <LoadingOverlay />}
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <div className={styles.modalIcon}>
                <Icon name="arrow-up-right" />
              </div>
              <div>
                <h3 className={styles.modalTitle}>Send Employer Request</h3>
                <p className={styles.modalSubtitle}>This action will send employer detail requests to selected students.</p>
              </div>
            </div>
            <div className={styles.modalTable}>
              <Table
                columns={[
                  {
                    header: 'Student',
                    key: 'student',
                    render: (row) => (
                      <div className={styles.studentCell}>
                        <ProfilePicture firstName={row.firstName} lastName={row.lastName} src={row.profilePicture} size="xs" />
                        <span>{row.firstName} {row.lastName}</span>
                      </div>
                    ),
                  },
                  { header: 'Email', key: 'email', render: (row) => row.email || '—' },
                ]}
                data={students.filter(s => selectedStudentIds.includes(s.id))}
                emptyMessage="No students selected."
              />
            </div>
            <div className={styles.modalActions}>
              <Button
                label="Cancel"
                variant="primary-light"
                size="small"
                onClick={() => setShowConfirmModal(false)}
              />
              <Button
                label={
                  <span className={styles.sendBtn}>
                    <Icon name="arrow-up-right" />
                    Send Request
                  </span>
                }
                variant="primary"
                size="small"
                onClick={handleSendRequests}
                disabled={isSending}
              />
            </div>
          </div>
        </Modal>
      )}

      {selectedDetail && (
        <Modal>
          <div className={styles.detailModal}>
            <div className={styles.detailHeader}>
              <div>
                <h3 className={styles.modalTitle}>Employer Details</h3>
                {selectedDetail.student && (
                  <p className={styles.modalSubtitle}>
                    {selectedDetail.student.firstName} {selectedDetail.student.lastName} &middot; {selectedDetail.student.email}
                  </p>
                )}
              </div>
              <button className={styles.closeBtn} onClick={() => setSelectedDetail(null)}>✕</button>
            </div>

            <div className={styles.section}>
              <h4 className={styles.sectionTitle}>Employment</h4>
              <div className={styles.detailGrid}>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Employer Name</span>
                  <span className={styles.detailValue}>{selectedDetail.employerName}</span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Job Title</span>
                  <span className={styles.detailValue}>{selectedDetail.jobTitle}</span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Employment Type</span>
                  <span className={styles.detailValue}>{selectedDetail.employmentType}</span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Start Date</span>
                  <span className={styles.detailValue}>{new Date(selectedDetail.employmentStartDate).toLocaleDateString()}</span>
                </div>
              </div>
            </div>

            <div className={styles.section}>
              <h4 className={styles.sectionTitle}>Contact</h4>
              <div className={styles.detailGrid}>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Contact Name</span>
                  <span className={styles.detailValue}>{selectedDetail.employerContactName || '—'}</span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Contact Email</span>
                  <span className={styles.detailValue}>{selectedDetail.employerContactEmail || '—'}</span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Contact Phone</span>
                  <span className={styles.detailValue}>{selectedDetail.employerContactPhone || '—'}</span>
                </div>
              </div>
            </div>

            <div className={styles.section}>
              <h4 className={styles.sectionTitle}>Notes</h4>
              <p className={styles.notes}>{selectedDetail.additionalNotes || '—'}</p>
            </div>

            <div className={styles.modalActions}>
              <Button label="Close" variant="white" size="small" onClick={() => setSelectedDetail(null)} />
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default EmployerDetails;
